import { useState } from "react";

import { useCustomerList } from "../../../../helpers/hooks/use-customer-list";
import type { CustomerRow } from "../../../../types/crm";

interface LinkAccountDialogProps {
  subjectUserId: string;
  onClose: () => void;
  onConfirm: (otherUserId: string) => void;
  isSubmitting: boolean;
}

export function LinkAccountDialog({
  subjectUserId,
  onClose,
  onConfirm,
  isSubmitting,
}: LinkAccountDialogProps) {
  const [keyword, setKeyword] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const { data, isLoading } = useCustomerList();

  const q = keyword.trim().toLowerCase();
  const candidates: CustomerRow[] = (data?.rows ?? [])
    .filter((r) => r.id !== subjectUserId)
    .filter((r) => {
      if (!q) return true;
      const name = (r.hotelName ?? "").toLowerCase();
      const email = r.primaryEmail.toLowerCase();
      return name.includes(q) || email.includes(q);
    })
    .slice(0, 50);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="link-account-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
    >
      <div className="w-[min(480px,90vw)] rounded-[10px] bg-surface-primary p-4 shadow-lg border border-border-secondary">
        <header className="flex items-center justify-between mb-3">
          <h4 className="text-content-primary text-[14px] font-semibold">
            Link to another account
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-content-tertiary hover:text-content-primary"
          >
            ✕
          </button>
        </header>

        <input
          type="text"
          placeholder="Search hotel name or email…"
          value={keyword}
          autoFocus
          onChange={(e) => setKeyword(e.target.value)}
          data-testid="link-account-search-input"
          className="w-full rounded-md border border-border-primary px-2.5 py-1.5 text-[13px] text-content-primary"
        />

        <div className="mt-3 max-h-72 overflow-y-auto">
          {isLoading && (
            <div className="text-content-tertiary text-[12px] px-1 py-2">
              Loading accounts…
            </div>
          )}
          {!isLoading && !q && candidates.length > 0 && (
            <div className="text-content-tertiary text-[11px] px-1 pb-1">
              Showing all accounts — type to filter.
            </div>
          )}
          {!isLoading && candidates.length === 0 && (
            <div className="text-content-tertiary text-[12px] px-1 py-2">
              No accounts match.
            </div>
          )}
          {candidates.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => setSelectedId(r.id)}
              data-testid={`link-candidate-${r.id}`}
              className={`w-full text-left px-2 py-2 rounded-md flex items-center justify-between gap-2 ${
                selectedId === r.id
                  ? "bg-surface-secondary"
                  : "hover:bg-surface-secondary/50"
              }`}
            >
              <div className="min-w-0">
                <div className="text-content-primary text-[13px] font-medium truncate">
                  {r.hotelName ?? "(no hotel name)"}
                </div>
                <div className="text-content-tertiary text-[12px] truncate">
                  {r.primaryEmail}
                </div>
              </div>
            </button>
          ))}
        </div>

        {serverError && (
          <div
            role="alert"
            className="mt-2 text-[12px] text-danger px-2 py-1 rounded-md bg-danger/10"
          >
            {serverError}
          </div>
        )}

        <footer className="mt-3 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-[12px] font-medium px-3 py-1.5 rounded-md border border-border-primary text-content-primary hover:bg-surface-secondary/50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!selectedId || isSubmitting}
            data-testid="link-account-confirm"
            onClick={() => {
              if (!selectedId) return;
              setServerError(null);
              try {
                onConfirm(selectedId);
              } catch (e) {
                setServerError(
                  e instanceof Error
                    ? e.message
                    : "Unable to link account.",
                );
              }
            }}
            className="text-[12px] font-medium px-3 py-1.5 rounded-md bg-brand-primary text-content-inverse hover:opacity-90 disabled:opacity-50"
          >
            {isSubmitting ? "Linking…" : "Link"}
          </button>
        </footer>
      </div>
    </div>
  );
}
